import type { CSSProperties, ReactNode } from "react";

import { cx, styled } from "simplydo/react/styled";

export type DividerProps = {
  horizontal?: boolean;
  vertical?: boolean;
  clearing?: boolean;
  fitted?: boolean;
  hidden?: boolean;
  section?: boolean;
  content?: ReactNode;
  children?: ReactNode;
  className?: string;
  style?: CSSProperties;
};

const StyledDivider = styled.div`
  border: none;
  margin: 1rem 0;
  height: 0;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(0, 0, 0, 0.85);
  user-select: none;
  -webkit-tap-highlight-color: transparent;
  font-size: 0.85714286rem;
  line-height: 1;
  border-top: 1px solid rgba(34, 36, 38, 0.15);

  &.is-fitted {
    margin: 0;
  }

  &.is-section {
    margin: 2rem 0;
  }

  &.is-clearing {
    clear: both;
  }

  &.is-hidden {
    border-color: transparent;
    background: none !important;
    &::before,
    &::after {
      display: none !important;
    }
  }

  &.is-vertical {
    position: absolute;
    z-index: 2;
    top: 50%;
    left: 50%;
    margin: 0;
    padding: 0;
    width: auto;
    height: 50%;
    line-height: 0;
    text-align: center;
    transform: translateX(-50%);
    border: none;
  }

  &.is-vertical:not(.is-hidden)::before,
  &.is-vertical:not(.is-hidden)::after {
    position: absolute;
    left: 50%;
    content: "";
    z-index: 3;
    border-left: 1px solid rgba(34, 36, 38, 0.15);
    width: 0;
    height: calc(100% - 2rem);
  }

  &.is-vertical:not(.is-hidden)::before {
    top: calc(-100% + 1rem);
  }

  &.is-vertical:not(.is-hidden)::after {
    top: auto;
    bottom: 1rem;
  }

  &.is-has-label:not(.is-vertical),
  &.is-horizontal:not(.is-has-label) {
    display: flex;
    align-items: center;
    text-align: center;
    height: auto;
    border-top: none;
  }

  &.is-has-label:not(.is-vertical):not(.is-hidden)::before,
  &.is-has-label:not(.is-vertical):not(.is-hidden)::after,
  &.is-horizontal:not(.is-has-label):not(.is-hidden)::before,
  &.is-horizontal:not(.is-has-label):not(.is-hidden)::after {
    content: "";
    flex: 1;
    border-top: 1px solid rgba(34, 36, 38, 0.15);
  }

  &.is-has-label:not(.is-vertical):not(.is-hidden)::before,
  &.is-horizontal:not(.is-has-label):not(.is-hidden)::before {
    margin-right: 1rem;
  }

  &.is-has-label:not(.is-vertical):not(.is-hidden)::after,
  &.is-horizontal:not(.is-has-label):not(.is-hidden)::after {
    margin-left: 1rem;
  }
`;

const Label = styled.span`
  display: inline-block;
  white-space: nowrap;
`;
const DividerComponent = (props: DividerProps) => {
  const {
    horizontal,
    vertical,
    clearing,
    fitted,
    hidden,
    section,
    content,
    children,
    className,
    style,
  } = props;
  const body = content !== undefined ? content : children;
  const hasLabel = body != null && body !== false;

  return (
    <StyledDivider
      role="separator"
      className={cx(
        fitted && "is-fitted",
        section && "is-section",
        clearing && "is-clearing",
        hidden && "is-hidden",
        vertical && "is-vertical",
        horizontal && "is-horizontal",
        hasLabel && "is-has-label",
        className,
      )}
      style={style}
    >
      {hasLabel ? <Label>{body}</Label> : null}
    </StyledDivider>
  );
};

export const Divider = styled(DividerComponent)``;
